import React, { useEffect, useState, useCallback } from 'react';
import { EmailApi } from '../../services/ApiService';
import { useToast } from '../../contexts/ToastContext';
import { Link, useNavigate } from 'react-router-dom';

export default function EmailList() {
  const [emails, setEmails] = useState([]);
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendTemplateId, setSendTemplateId] = useState(null);
  const [sendTemplateKey, setSendTemplateKey] = useState('');
  const [recipientInput, setRecipientInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sendTemplateActive, setSendTemplateActive] = useState(true);
  const [fieldsInput, setFieldsInput] = useState('');
  const [fieldsParseError, setFieldsParseError] = useState(null);

  const fetchEmails = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await EmailApi.getEmails({ page, size });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const data = await res.json();
      // New response format: { size, totalPages, page, items: [...], totalElements }
      const list = Array.isArray(data.items) ? data.items : [];
      setEmails(list);
      setTotalPages(typeof data.totalPages === 'number' ? data.totalPages : 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [page, size]);

  useEffect(() => { fetchEmails(); }, [fetchEmails]);

  const openSendModal = (id) => {
    const tpl = emails.find(t => (t.id || t.emailId) === id);
    setSendTemplateId(id);
    setSendTemplateKey(tpl?.templateKey || '');
    setSendTemplateActive(tpl ? !!tpl.isActive : true);
    setRecipientInput('');
    setSendError(null);
    setSendModalOpen(true);
  };

  const closeSendModal = () => {
    if (sending) return; // prevent closing mid-request
    setSendModalOpen(false);
  setSendTemplateId(null);
  setSendTemplateKey('');
    setRecipientInput('');
    setSendError(null);
  };

  const parseRecipients = (raw) => {
    return raw
      .split(/[,\n;]/)
      .map(r => r.trim())
      .filter(r => r.length > 0);
  };

  const handleConfirmSend = async (e) => {
    e.preventDefault();
    if (!sendTemplateId || !sendTemplateKey) {
      setSendError('Missing template key.');
      return;
    }
    const recipients = parseRecipients(recipientInput);
    if (recipients.length === 0) {
      setSendError('Enter at least one email address.');
      return;
    }
    // simple client validation
    const invalid = recipients.filter(r => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(r));
    if (invalid.length) {
      setSendError(`Invalid emails: ${invalid.slice(0,5).join(', ')}${invalid.length>5?'…':''}`);
      return;
    }
    // parse dynamic fields JSON (optional)
    let fields = {};
    setFieldsParseError(null);
    const trimmed = fieldsInput.trim();
    if (trimmed) {
      try {
        fields = JSON.parse(trimmed);
        if (fields === null || typeof fields !== 'object' || Array.isArray(fields)) {
          throw new Error('Fields must be a JSON object.');
        }
      } catch (err) {
        setFieldsParseError(err.message);
        return;
      }
    }
    setSending(true); setSendError(null);
    try {
      let successCount = 0;
      for (const r of recipients) {
        const res = await EmailApi.sendEmail(sendTemplateKey, r, fields);
        if (res.ok) {
          successCount++;
        } else {
          const txt = await res.text();
          addToast({ intent: 'danger', message: `Failed ${r}: ${txt || res.status}` });
        }
      }
      if (successCount) {
        addToast({ intent: 'success', message: `Queued ${successCount} email${successCount>1?'s':''}.` });
        closeSendModal();
      } else {
        setSendError('All sends failed.');
      }
    } catch (err) {
      setSendError(err.message);
      addToast({ intent: 'danger', message: `Error sending: ${err.message}` });
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this email draft?')) return;
    try {
      const res = await EmailApi.deleteEmail(id);
      if (!res.ok) throw new Error('Delete failed');
      addToast({ intent: 'success', message: 'Email deleted.' });
      fetchEmails();
    } catch (e) {
      addToast({ intent: 'danger', message: `Error deleting email: ${e.message}` });
    }
  };

  return (
    <>
  <div className="email-list-page settings-theme-panel" style={{fontSize:'var(--font-base,14px)'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem'}}>
  <h3 style={{margin:0,fontSize:'var(--font-lg,1.4rem)',fontWeight:600}}>Email Templates</h3>
  <button className="btn-primary btn-sm" onClick={() => navigate('/settings/emails/create')}>New Template</button>
      </div>
  <div className="settings-theme-panel" style={{padding:'14px 16px',marginBottom:'20px'}}>
        <p style={{margin:'0 0 8px',fontSize:'var(--font-sm,0.85rem)',fontWeight:600}}>How to use templates</p>
        <ul style={{margin:0,paddingLeft:'20px',fontSize:'var(--font-xs,0.75rem)',display:'flex',flexDirection:'column',gap:'6px',lineHeight:'1.3'}}>
          <li><strong>Key</strong> identifies the template when sending (<code>templateKey</code>).</li>
          <li><strong>Active</strong> must be Yes to allow sending.</li>
          <li>Placeholders use <code>{'{{name}}'}</code> syntax; define them in subject/body and supply values when sending.</li>
          <li>Use Send for one-off emails. Enter multiple addresses separated by commas, semicolons, or newlines.</li>
          <li>Provide both HTML and Text bodies to improve deliverability & accessibility.</li>
          <li><strong>Back-End Hardcoded Values:</strong> Invitation email template must be <strong>"invitation"</strong></li>
          <li><strong>Back-End Hardcoded Values:</strong> requestPasswordReset email template must be <strong>"password-reset"</strong></li>
        </ul>
      </div>
      {error && <div className="error-message" role="alert">{error}</div>}
      {loading ? (
        <div className="loading-state"><div className="loading-spinner" />Loading emails...</div>
      ) : emails.length === 0 ? (
        <div className="empty-state"><h3>No Emails</h3><p>Create one to begin.</p></div>
      ) : (
  <div className="settings-theme-table-wrapper"><table className="settings-theme-table" style={{fontSize:'var(--font-sm,0.85rem)'}}>
          <thead>
            <tr>
              <th>Key</th>
              <th>Subject</th>
              <th>Active</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {emails.map(e => (
              <tr key={e.id || e.emailId}>
                <td>{e.templateKey || e.key || (e.id || e.emailId)}</td>
                <td>{e.subject}</td>
                <td>{typeof e.isActive === 'boolean' ? (e.isActive ? 'Yes' : 'No') : '—'}</td>
                <td>{e.updatedAt ? new Date(e.updatedAt).toLocaleString() : '—'}</td>
                <td style={{display:'flex',gap:'4px'}}>
                  <Link to={`/settings/emails/${e.id || e.emailId}`} className="btn-link">Edit</Link>
                  <button className="btn-secondary btn-sm" onClick={() => openSendModal(e.id || e.emailId)}>Send</button>
                  <button className="btn-danger btn-sm" onClick={() => handleDelete(e.id || e.emailId)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
  </table></div>
      )}
      {totalPages > 1 && (
        <div style={{display:'flex',justifyContent:'center',marginTop:'1rem',gap:'8px'}}>
          <button disabled={page===0} className="btn-outline btn-sm" onClick={()=>setPage(p=>Math.max(0,p-1))}>Prev</button>
          <span style={{fontSize:'var(--font-xs,0.75rem)',alignSelf:'center'}}>Page {page+1} / {totalPages}</span>
          <button disabled={page>=totalPages-1} className="btn-outline btn-sm" onClick={()=>setPage(p=>Math.min(totalPages-1,p+1))}>Next</button>
        </div>
      )}
    </div>
    {sendModalOpen && (
      <div role="dialog" aria-modal="true" style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
  <div style={{background:'var(--surface-elevated,#fff)',color:'var(--text-primary,#222)',padding:'24px',borderRadius:'10px',width:'min(640px,90%)',boxShadow:'0 10px 30px -5px rgba(0,0,0,0.25)',display:'flex',flexDirection:'column',gap:'18px',fontSize:'var(--font-sm,0.9rem)'}}>
          <h4 style={{margin:0,fontSize:'1rem'}}>Send Template {sendTemplateKey && <code style={{fontSize:'.65rem'}}>{sendTemplateKey}</code>}</h4>
          <form onSubmit={handleConfirmSend} style={{display:'flex',flexDirection:'column',gap:'16px'}}>
            {!sendTemplateActive && (
              <div role="alert" style={{background:'#fff8e1',color:'#7a5c00',padding:'8px 10px',borderRadius:'4px',fontSize:'.65rem',border:'1px solid #f5d061'}}>
                This template is currently inactive. Activate it first in Edit view to send.
              </div>
            )}
            <label style={{display:'flex',flexDirection:'column',gap:'6px',fontSize:'var(--font-xs,0.75rem)',fontWeight:600}}>Recipients
              <textarea
                rows={6}
                value={recipientInput}
                onChange={e=>setRecipientInput(e.target.value)}
                placeholder="one@example.com, two@example.com\nor\nline-separated@example.com"
                style={{padding:'8px 10px',fontFamily:'monospace'}}
                required
              />
              <span style={{fontSize:'var(--font-xs,0.7rem)',color:'var(--text-subtle,#555)'}}>Comma, semicolon, or newline separated. Validated locally.</span>
            </label>
            <label style={{display:'flex',flexDirection:'column',gap:'6px',fontSize:'var(--font-xs,0.75rem)',fontWeight:600}}>Fields JSON (optional)
              <textarea
                rows={6}
                value={fieldsInput}
                onChange={e=>setFieldsInput(e.target.value)}
                placeholder='{"firstName":"Alice","resetLink":"https://app/reset"}'
                style={{padding:'8px 10px',fontFamily:'monospace'}}
              />
              <span style={{fontSize:'var(--font-xs,0.7rem)',color: fieldsParseError ? 'var(--color-danger-text,#900)' : 'var(--text-subtle,#555)'}}>
                {fieldsParseError ? fieldsParseError : 'Provide a JSON object mapping placeholder keys to values.'}
              </span>
            </label>
            {sendError && <div role="alert" style={{background:'var(--color-danger-bg,#fee)',color:'var(--color-danger-text,#900)',padding:'8px',borderRadius:'4px',fontSize:'var(--font-xs,0.7rem)'}}>{sendError}</div>}
            <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
              <button type="submit" className="btn-primary btn-sm" disabled={sending || !sendTemplateActive}>{sending ? 'Sending…' : 'Send Now'}</button>
              <button type="button" className="btn-secondary btn-sm" onClick={closeSendModal} disabled={sending}>Cancel</button>
            </div>
          </form>
        </div>
      </div>
    )}
    </>
  );
}
